import { createFileRoute, notFound } from "@tanstack/react-router";
import { TrArchive, archivePageCount } from "@/components/site/TrArchive";
import { getAllPosts } from "@/lib/posts.functions";
import { listApprovedThemes } from "@/lib/themes-public.functions";
import { pageTitle } from "@/lib/brand";
import { hreflangLinks } from "@/lib/seo";

const ARCHIVE_URL = "https://akillisurdurulebilirlik.com/yazilar";

export const Route = createFileRoute("/yazilar/")({
  // ?sayfa=N is a crawlable server-rendered page of the archive; page 1 has no param.
  validateSearch: (s: Record<string, unknown>): { sayfa?: number } => {
    const n = Number(s.sayfa);
    return Number.isInteger(n) && n >= 2 ? { sayfa: n } : {};
  },
  loaderDeps: ({ search }) => ({ sayfa: search.sayfa ?? 1 }),
  loader: async ({ deps }) => {
    const [posts, themes] = await Promise.all([getAllPosts({ data: { lang: "tr" } }), listApprovedThemes()]);
    const page = deps.sayfa;
    if (page > archivePageCount(posts)) throw notFound();
    return { posts, themes, page };
  },
  head: ({ loaderData }) => {
    const page = loaderData?.page ?? 1;
    const selfUrl = page > 1 ? `${ARCHIVE_URL}?sayfa=${page}` : ARCHIVE_URL;
    return {
    scripts: page > 1 ? [] : [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${ARCHIVE_URL}#collection`,
          name: "Yazılar",
          url: ARCHIVE_URL,
          inLanguage: "tr-TR",
          isPartOf: { "@id": "https://akillisurdurulebilirlik.com/#website" },
          publisher: { "@id": "https://akillisurdurulebilirlik.com/#publisher" },
          author: { "@id": "https://akillisurdurulebilirlik.com/yazar#person" },
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: loaderData?.posts.length ?? 0,
            itemListOrder: "https://schema.org/ItemListOrderDescending",
            itemListElement: (loaderData?.posts ?? []).map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${ARCHIVE_URL}/${p.slug}`,
              name: p.title,
            })),
          },
        }),
      },
    ],
    meta: [
      { title: page > 1 ? pageTitle(`Yazılar — Sayfa ${page}`, "tr") : pageTitle("Yazılar", "tr") },
      {
        name: "description",
        content:
          "Arda Öztaşkın'ın yapay zekâ, iklim, sürdürülebilirlik ve COP31 üzerine tüm yazıları. Etiket ve arama ile arşivi keşfedin.",
      },
      { property: "og:title", content: "Yazılar — Akıllı Sürdürülebilirlik" },
      {
        property: "og:description",
        content:
          "Arda Öztaşkın'ın yapay zekâ, iklim, sürdürülebilirlik ve COP31 üzerine tüm yazıları. Etiket ve arama ile arşivi keşfedin.",
      },
      { property: "og:url", content: selfUrl },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: selfUrl },
      ...(page > 1 ? [] : hreflangLinks("/yazilar", "/ideas")),
    ],
    };
  },
  component: ArchiveRoute,
});

function ArchiveRoute() {
  const { posts, themes, page } = Route.useLoaderData();
  return <TrArchive key={page} posts={posts} themes={themes} page={page} />;
}
